import React, { useState } from 'react';
import { Search, ChevronDown, ChevronUp, Home, Calendar, CreditCard, User, Shield, Star, Send, CheckCircle, HelpCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { faqFeedbackAPI } from '../services/api';

const FAQFeedback = () => {
  const { getAuthToken } = useAuth();
  const { toast } = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [openFaq, setOpenFaq] = useState(null);
  const [selectedCat, setSelectedCat] = useState('All');

  // Feedback form
  const [feedbackCategory, setFeedbackCategory] = useState('');
  const [challenges, setChallenges] = useState('');
  const [suggestions, setSuggestions] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const feedbackCategories = [
    { value: 'booking', label: 'Booking & Stays' },
    { value: 'payments', label: 'Payments & Payouts' },
    { value: 'messaging', label: 'Messaging & Communication' },
    { value: 'food-delivery', label: 'Food & Delivery' },
    { value: 'rides', label: 'Rides & Transport' },
    { value: 'services', label: 'Home Services (Fix)' },
    { value: 'deals', label: 'Deal Marketplace (Invest)' },
    { value: 'referrals', label: 'Referrals & Earn' },
    { value: 'account', label: 'Account & Profile' },
    { value: 'ui-ux', label: 'UI / Design / Navigation' },
    { value: 'performance', label: 'Speed / Performance / Bugs' },
    { value: 'onboarding', label: 'Onboarding & Verification' },
    { value: 'other', label: 'Other' },
  ];

  const categories = [
    { id: 'All', label: 'All Topics', icon: <Home size={16} /> },
    { id: 'Safety', label: 'Trust & Safety', icon: <Shield size={16} /> },
    { id: 'Booking', label: 'Booking', icon: <Calendar size={16} /> },
    { id: 'Payments', label: 'Payments', icon: <CreditCard size={16} /> },
    { id: 'Account', label: 'Account', icon: <User size={16} /> },
    { id: 'Hosting', label: 'Hosting', icon: <Home size={16} /> },
    { id: 'Restaurants', label: 'Restaurant Partners', icon: <Home size={16} /> },
    { id: 'Reviews', label: 'Reviews', icon: <Star size={16} /> },
  ];

  const faqs = [
    { id: 1, cat: 'Booking', q: 'How do I make a booking?', a: 'Search for properties using our browse page, select your dates and guests, then click "Book Now". If the property has instant booking enabled, you\'ll be confirmed immediately. For request-to-book properties, the host has 24 hours to accept or decline.' },
    { id: 2, cat: 'Booking', q: 'Can I change my booking dates?', a: 'Date changes are subject to the host\'s cancellation policy and availability. Contact the host via the messaging system first to discuss, then submit a modification request from your booking dashboard.' },
    { id: 3, cat: 'Booking', q: 'What is the cancellation policy?', a: 'Cancellation policies vary by property — you\'ll see the policy clearly on each listing before booking. Policies range from Flexible (full refund up to 24 hours before check-in) to Strict (50% refund up to 7 days before).' },
    { id: 4, cat: 'Booking', q: 'What happens if a host cancels my booking?', a: 'If a host cancels a confirmed booking, you\'ll receive a full refund within 3–5 business days. We\'ll also help you find alternative accommodation and may provide a rebooking credit.' },
    { id: 5, cat: 'Payments', q: 'What payment methods do you accept?', a: 'We accept Visa, Mastercard, American Express, and PayPal. For longer stays (28+ nights), bank transfer options may be available. All transactions are processed securely through Stripe.' },
    { id: 6, cat: 'Payments', q: 'When does the host receive payment?', a: 'Payment is released to the host 24 hours after the guest\'s check-in time. Funds typically appear in the host\'s account within 3–5 business days after release.' },
    { id: 7, cat: 'Payments', q: 'Are there any extra fees?', a: 'Under our host only model, guests pay no separate service fee, so the price you see is the price you pay. Hosts pay a 13% platform commission on accommodation (founding hosts pay 0% for their first 90 days, then 10% for the rest of year one). All fees are shown clearly before you confirm your booking.' },
    { id: 8, cat: 'Payments', q: 'Is my payment information secure?', a: 'Yes. We use Stripe for all payment processing. Your card details are never stored on our servers and all transactions are encrypted with TLS.' },
    { id: 9, cat: 'Account', q: 'How do I verify my identity?', a: 'Go to your Profile Settings and click "Verify Identity". You\'ll need to upload a government-issued photo ID. Verification is processed within 1 business day.' },
    { id: 10, cat: 'Account', q: 'Can I have both a guest and host account?', a: 'One Ariv account can be used as both a guest and a host. Simply add the "Host" role in your account settings to unlock hosting features.' },
    { id: 11, cat: 'Account', q: 'How do I delete my account?', a: 'Go to Profile Settings → Account → Delete Account. You\'ll need to resolve any active bookings before deletion. Deleted accounts cannot be recovered.' },
    { id: 12, cat: 'Hosting', q: 'How do I become a host?', a: 'Click "Become a Host" in the navigation. Create a host account, verify your identity, add your property details and photos, set pricing, and agree to host terms. Your listing will be checked for completeness and compliance with platform policies within 24 hours.' },
    { id: 13, cat: 'Hosting', q: 'What fees do hosts pay?', a: 'Hosts pay a 13% commission on accommodation under our host only model, with no separate guest fee. No monthly fees, no listing fees, and no charge if you receive zero bookings. Founding hosts (the first 100) pay 0% for their first 90 days, then 10% for the rest of their first year.' },
    { id: 14, cat: 'Hosting', q: 'How does the Deal Marketplace work?', a: 'The Deal Marketplace connects landlords with Rent-to-Rent host operators who take on a property for an agreed, fixed rent. Landlords list properties; R2R hosts browse and make offers. All negotiations happen through our messaging system.' },
    { id: 15, cat: 'Hosting', q: 'How do I optimise my listing?', a: 'Key factors: high-quality photos (minimum 10), competitive pricing (use our smart pricing tool), fast response rate (under 1 hour), and positive reviews. Listings with 4.5+ stars get boosted in search results.' },
    { id: 16, cat: 'Safety', q: 'What checks happen before a listing goes live?', a: 'Listings are reviewed for completeness (address and photos) before going live. Hosts remain responsible for the condition and accuracy of their listing.' },
    { id: 17, cat: 'Safety', q: 'What if something goes wrong during a stay?', a: 'Contact the host via messaging first. If unresolved within 2 hours or urgent, contact our support team at hello@ariv.co.uk. For emergencies, always contact 999 first.' },
    { id: 18, cat: 'Safety', q: 'How are disputes handled?', a: 'Our Resolution Centre handles disputes. Either party can open a dispute within 72 hours of checkout. Our team reviews evidence and makes a binding decision within 5 business days.' },
    { id: 19, cat: 'Reviews', q: 'How does the review system work?', a: 'After checkout, both guests and hosts have 14 days to leave a review. Reviews are published simultaneously to prevent bias.' },
    { id: 20, cat: 'Reviews', q: 'Can I dispute an unfair review?', a: 'You can report reviews that violate policy (false info, threats, not from a genuine stay). We\'ll investigate and remove if it breaches guidelines.' },
    // Trust — kept deliberately plain English. Detailed engineering specifics live
    // inside the team, not on a public FAQ that anyone (including the curious
    // wrong sort of people) can read.
    { id: 21, cat: 'Safety', q: 'Is Ariv a real registered business?', a: 'Yes. Ariv is operated by Brick & Capital Ltd, registered in England and Wales (company number 16655788) and on the ICO data protection register (ZC129399). Both are public records you can verify on the official government sites.' },
    { id: 22, cat: 'Safety', q: 'Can anyone at Ariv see my password?', a: 'No. When you create a password we scramble it before saving and only keep the scrambled version. The scramble cannot be reversed, so nobody at Ariv (founder included) can see what your actual password is. If you forget it you reset it, not retrieve it.' },
    { id: 23, cat: 'Safety', q: 'What happens to my bank details and NI number?', a: 'They are locked away with a separate key the app holds. Even if our database were stolen, those fields would just look like random characters without the key. The unlocked versions are only seen by the parts of the app that actually need them, like sending your weekly payout to Stripe.' },
    { id: 24, cat: 'Safety', q: 'How do I turn on two factor authentication?', a: 'Sign in, open Settings, choose Security, then Enable 2FA. Scan the QR code with an app like Google Authenticator, Authy, or 1Password, and save the backup codes somewhere safe. From then on, signing in needs both your password and a 6 digit code from your phone.' },
    { id: 25, cat: 'Safety', q: 'Are my card payments safe?', a: 'Yes. Card details are handled by Stripe (the payments company behind a lot of the big shops and apps you already use). Your card number never touches our servers and never sits in our database.' },
    { id: 26, cat: 'Safety', q: 'Do you sell my data?', a: 'No. We do not sell personal data to anyone. The data you give us is used to run the app you signed up for and to meet legal obligations like HMRC reporting for people who earn through Ariv. See our Privacy Policy for the full detail.' },
    { id: 27, cat: 'Safety', q: 'How do I delete my account and my data?', a: 'Go to Settings, then Account, then Delete Account. We remove your personal data in line with the law and our privacy policy. The only things we keep are records HMRC requires us to keep for a fixed number of years if you earned through the platform.' },
    { id: 28, cat: 'Safety', q: 'What if you find a security problem?', a: 'A monitoring tool watches the app around the clock and flags issues to us as they happen. When something needs fixing, we fix it and ship the fix straight away. If a problem ever affected your account, we would tell you what happened, what we did about it, and what you should do.' },
    // Restaurant Partners
    { id: 29, cat: 'Restaurants', q: 'I have my own riders. How quickly can I start taking orders?', a: 'As soon as Ariv approves your account (usually within one working day). Pick Self-Delivery on signup. You pay 12% commission and go live as soon as your menu is published.' },
    { id: 30, cat: 'Restaurants', q: 'I do not have my own riders — can Ariv deliver for me?', a: 'Eventually yes. Ariv is still building our delivery network. If you pick "Use Ariv riders" your restaurant joins our waiting list and shows as "Coming soon" until we have verified riders covering your area. You can still set up your menu and pricing in the meantime.' },
    { id: 31, cat: 'Restaurants', q: 'When will Ariv riders be available in my area?', a: 'Depends on rider supply in your postcode. We are rolling out zone by zone. Switch to self-delivery to start earning immediately and flip back to Ariv riders later from your dashboard.' },
    { id: 32, cat: 'Restaurants', q: 'Can I switch between self-delivery and Ariv riders?', a: 'Yes, any time. The toggle lives in your restaurant dashboard. Commission adjusts automatically: 10% for self-delivery, 20% for platform delivery.' },
    { id: 33, cat: 'Restaurants', q: 'What is the difference in commission?', a: '10% if you deliver yourself, 20% if Ariv delivers for you. The higher rate covers the rider, hire-and-reward insurance, dispatch tech, and delivery support.' },
    { id: 34, cat: 'Restaurants', q: 'How do I list my restaurant?', a: 'Go to /restaurant/signup. You need your restaurant name, an email, phone, address, FHRS hygiene rating, and bank details for payouts (encrypted at rest).' },
  ];

  const filtered = faqs.filter(f => {
    const catMatch = selectedCat === 'All' || f.cat === selectedCat;
    const searchMatch = !searchQuery || f.q.toLowerCase().includes(searchQuery.toLowerCase()) || f.a.toLowerCase().includes(searchQuery.toLowerCase());
    return catMatch && searchMatch;
  });

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    if (!challenges.trim() && !suggestions.trim()) {
      toast({ title: 'Please fill in at least one field', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const token = getAuthToken();
      await faqFeedbackAPI.submit(token, { category: feedbackCategory, challenges, suggestions });
      setSubmitted(true);
      setFeedbackCategory('');
      setChallenges('');
      setSuggestions('');
      toast({ title: 'Thank you!', description: 'Your feedback has been submitted.' });
    } catch (err) {
      toast({ title: 'Error', description: 'Could not submit feedback. Please try again.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* FAQ Section */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle size={22} className="text-[#1F3A68]" />
          <h2 className="text-xl font-bold text-gray-800">Frequently Asked Questions</h2>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search FAQs..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30"
          />
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap gap-1.5 mb-6">
          {categories.map(c => (
            <button
              key={c.id}
              onClick={() => setSelectedCat(c.id)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                selectedCat === c.id ? 'bg-[#1F3A68] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {c.icon}{c.label}
            </button>
          ))}
        </div>

        {/* FAQ Items */}
        <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
          {filtered.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              <Search size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">No results found.</p>
            </div>
          )}
          {filtered.map(faq => (
            <div key={faq.id} className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                className="w-full flex items-center justify-between px-4 py-3.5 text-left"
              >
                <div className="flex items-center gap-2">
                  <span className="text-[10px] bg-[#1F3A68]/10 text-[#1F3A68] px-2 py-0.5 rounded-full font-medium">{faq.cat}</span>
                  <span className="font-medium text-gray-800 text-sm">{faq.q}</span>
                </div>
                {openFaq === faq.id ? <ChevronUp size={16} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />}
              </button>
              {openFaq === faq.id && (
                <div className="px-4 pb-4 pt-0 border-t border-gray-100">
                  <p className="text-gray-600 text-sm leading-relaxed pt-3">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Feedback Form */}
      <div className="bg-gradient-to-br from-[#1F3A68]/5 to-[#C9AA71]/10 rounded-2xl p-6 border border-[#1F3A68]/10">
        <h3 className="text-lg font-bold text-gray-800 mb-1">We'd love your feedback</h3>
        <p className="text-sm text-gray-500 mb-5">Help us improve Ariv by sharing your experience.</p>

        {submitted ? (
          <div className="text-center py-8">
            <CheckCircle size={48} className="text-green-500 mx-auto mb-3" />
            <p className="font-semibold text-gray-800">Thank you for your feedback!</p>
            <p className="text-sm text-gray-500 mt-1">We'll use it to make Ariv better.</p>
            <button
              onClick={() => setSubmitted(false)}
              className="mt-4 text-sm text-[#1F3A68] font-medium hover:underline"
            >
              Submit more feedback
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmitFeedback} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Category <span className="text-red-400">*</span>
              </label>
              <select
                value={feedbackCategory}
                onChange={e => setFeedbackCategory(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30 bg-white"
                required
              >
                <option value="">Select what area this is about...</option>
                {feedbackCategories.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                What challenges have you encountered?
              </label>
              <textarea
                value={challenges}
                onChange={e => setChallenges(e.target.value)}
                rows={3}
                placeholder="Tell us about any issues or difficulties you've faced..."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30 resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Any suggestions on how we can improve?
              </label>
              <textarea
                value={suggestions}
                onChange={e => setSuggestions(e.target.value)}
                rows={3}
                placeholder="We'd love to hear your ideas for making Ariv better..."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30 resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={submitting || !feedbackCategory || (!challenges.trim() && !suggestions.trim())}
              className="flex items-center gap-2 bg-[#1F3A68] text-white px-6 py-2.5 rounded-xl text-sm font-medium hover:bg-[#1F3A68]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send size={16} />
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default FAQFeedback;
